import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, Compass, Feather, BookOpen, Activity, Moon, Heart, Brain, Eye, Atom, Volume2, VolumeX, Sun,
} from "lucide-react";
import { STOIC_ARTICLE } from "@/lib/stoicContent";
import { endpoints } from "@/lib/api";
import SoulOrb from "@/components/SoulOrb";
import { canSpeak, speak, stopSpeaking } from "@/lib/readAloud";
/**
 * Quiet Reads — a small library of short, gender-neutral essays that live on
 * the Mind → A Quiet Read tab. Two shapes exported here:
 *
 *   • QuietReadCard   — the tap-to-open teaser (used in the library grid).
 *   • QuietReadReader — the full modal reader; accepts an `article` object.
 *
 * The Stoicism piece is still exported under its old names (StoicArticleCard,
 * StoicArticleReader) so anything still importing them keeps working.
 */

const ACCENT_ICONS = { Compass, Activity, Moon, Heart, Brain, Eye, Atom, Sun };

export function QuietReadCard({ article, onOpen }) {
  const Icon = ACCENT_ICONS[article.accent] || Compass;
  if (article.featured) return <FeaturedQuietReadCard article={article} onOpen={onOpen} Icon={Icon} />;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`quiet-read-card-${article.slug}`}
      className="group w-full text-left relative overflow-hidden rounded-md border hairline bg-gradient-to-br from-[hsl(var(--sand))]/40 via-[hsl(var(--paper))] to-[hsl(var(--paper))] tab-link hover:shadow-[0_4px_20px_-10px_hsl(var(--clay)/0.35)]"
    >
      {/* Slim therapeutic image band — kept short so it never overpowers */}
      {article.hero_image && (
        <div
          className="relative aspect-[21/5] bg-center bg-cover"
          style={{ backgroundImage: `url("${article.hero_image}")` }}
          aria-hidden
        >
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(30,23,18,0.10) 0%, rgba(30,23,18,0.42) 100%)",
            }}
          />
        </div>
      )}
      <div className="p-6 sm:p-7">
        <div className="flex items-start gap-4">
          <span
            aria-hidden
            className="shrink-0 grid place-items-center w-11 h-11 rounded-full bg-[hsl(var(--clay))]/12 text-[hsl(var(--clay))]"
          >
            <Icon size={16} />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--clay))]">
              A quiet read · {article.minutes || 4} minutes
            </div>
            <div className="mt-1 font-display text-xl sm:text-2xl leading-tight text-[hsl(var(--fg))]">
              {article.title}
            </div>
            {article.subtitle && (
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                {article.subtitle}
              </p>
            )}
            <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--terracotta))] group-hover:gap-2 transition-all">
              <BookOpen size={11} />
              Open the read
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * FeaturedQuietReadCard — a larger, image-led editorial teaser used for
 * articles marked `featured: true`. Deliberately spans the full grid
 * width so it reads as a visual anchor rather than another tile.
 */
function FeaturedQuietReadCard({ article, onOpen, Icon }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`quiet-read-card-${article.slug}`}
      className="group md:col-span-2 w-full text-left relative overflow-hidden rounded-[20px] border hairline bg-[hsl(var(--paper))] tab-link hover:shadow-[0_10px_36px_-18px_hsl(var(--clay)/0.45)] transition-shadow"
    >
      {article.hero_image && (
        <div
          className="relative aspect-[21/4] bg-center bg-cover"
          style={{ backgroundImage: `url("${article.hero_image}")` }}
        >
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(30,23,18,0.10) 0%, rgba(30,23,18,0.42) 100%)",
            }}
          />
          {article.feature_label && (
            <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] bg-[hsl(var(--paper))]/85 backdrop-blur-sm text-[hsl(var(--copper))]">
              {article.feature_label}
            </div>
          )}
        </div>
      )}
      <div className="p-6 sm:p-9">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-[hsl(var(--clay))]/12 text-[hsl(var(--clay))]"
          >
            <Icon size={14} />
          </span>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--clay))]">
            A quiet read · {article.minutes || 4} minutes
          </div>
        </div>
        <h3 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-[hsl(var(--fg))]">
          {article.title}
        </h3>
        {article.subtitle && (
          <p className="mt-4 font-display italic text-lg md:text-xl text-[hsl(var(--clay))] leading-snug max-w-2xl">
            {article.subtitle}
          </p>
        )}
        {article.card_preview && (
          <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--fg))]/80 max-w-2xl">
            {article.card_preview}
          </p>
        )}
        <div className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--terracotta))] group-hover:gap-3 transition-all">
          <BookOpen size={12} />
          Read quietly
          <span aria-hidden>→</span>
        </div>
      </div>
    </button>
  );
}

/** Backward-compat wrapper — always opens the Stoicism piece. */
export function StoicArticleCard({ onOpen }) {
  return <QuietReadCard article={STOIC_ARTICLE} onOpen={onOpen} />;
}


export function QuietReadReader({ open, article, onClose, onSeeded }) {
  const [pulseSeed, setPulseSeed] = useState(0);
  const [readingIdx, setReadingIdx] = useState(-1);
  const [readingQuote, setReadingQuote] = useState("");
  const [readingFull, setReadingFull] = useState(false);
  const scrollRef = React.useRef(null);
  const [hasBookmark, setHasBookmark] = useState(false);
  const bookmarkKey = article?.slug ? `qr:${article.slug}:pos` : null;

  // Compose the full readable article for the top-level "Listen" button.
  const composeArticleText = React.useCallback((a) => {
    if (!a) return "";
    const parts = [];
    parts.push(a.title + ".");
    if (a.subtitle) parts.push(a.subtitle + ".");
    if (a.intro) parts.push(a.intro);
    for (const s of a.sections || []) {
      parts.push(s.heading + ".");
      if (s.body) parts.push(s.body);
      if (Array.isArray(s.bullets)) for (const b of s.bullets) parts.push(b);
      if (Array.isArray(s.subsections))
        for (const sub of s.subsections)
          parts.push(`${sub.title}. ${sub.body}`);
      if (s.closing) parts.push(s.closing);
      if (s.pull_quote) parts.push(s.pull_quote);
    }
    if (a.emphasized_prompt) parts.push(a.emphasized_prompt);
    if (Array.isArray(a.epilogue)) for (const line of a.epilogue) parts.push(line);
    return parts.join(" ");
  }, []);

  const toggleFullRead = () => {
    if (readingFull) {
      stopSpeaking();
      setReadingFull(false);
      return;
    }
    if (!canSpeak()) {
      toast.error("Read aloud isn't available on this browser.");
      return;
    }
    // Cancel any single-item reads first
    stopSpeaking();
    setReadingIdx(-1);
    setReadingQuote("");
    const text = composeArticleText(article);
    speak(text, { rate: 0.85, onEnd: () => setReadingFull(false) });
    setReadingFull(true);
  };

  // Stop any in-flight playback when the reader closes or the article changes.
  useEffect(() => {
    if (!open) {
      stopSpeaking();
      setReadingIdx(-1);
      setReadingQuote("");
      setReadingFull(false);
    }
  }, [open, article?.slug]);
  useEffect(() => () => stopSpeaking(), []);

  // Bookmark: on open, detect a saved position; on scroll, save it.
  useEffect(() => {
    if (!open || !bookmarkKey) return;
    try {
      const raw = localStorage.getItem(bookmarkKey);
      const saved = raw ? parseInt(raw, 10) : 0;
      setHasBookmark(Number.isFinite(saved) && saved > 240);
    } catch {
      setHasBookmark(false);
    }
  }, [open, bookmarkKey]);

  useEffect(() => {
    if (!open || !bookmarkKey) return;
    const el = scrollRef.current;
    if (!el) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const top = el.scrollTop;
        try {
          // Near the bottom → treat as finished and clear bookmark.
          const nearEnd = top + el.clientHeight >= el.scrollHeight - 200;
          if (nearEnd) localStorage.removeItem(bookmarkKey);
          else localStorage.setItem(bookmarkKey, String(Math.round(top)));
        } catch { /* noop */ }
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
    };
  }, [open, bookmarkKey]);

  const resumeBookmark = () => {
    if (!bookmarkKey) return;
    try {
      const raw = localStorage.getItem(bookmarkKey);
      const saved = raw ? parseInt(raw, 10) : 0;
      if (Number.isFinite(saved) && saved > 0 && scrollRef.current) {
        scrollRef.current.scrollTo({ top: saved, behavior: "smooth" });
        setHasBookmark(false);
      }
    } catch { /* noop */ }
  };

  if (!open || !article) return null;

  const seedPrompt = async (text) => {
    try {
      await endpoints.createJournal({ prompt: text, content: "", mood_tag: "reflect" });
      setPulseSeed((p) => p + 1);
      toast.success("Seeded into your Journal — begin whenever you're ready.");
      onSeeded?.();
    } catch {
      toast.error("Could not seed. Try again in a moment.");
    }
  };

  const togglePromptRead = (i, text) => {
    if (readingIdx === i) {
      stopSpeaking();
      setReadingIdx(-1);
      return;
    }
    if (!canSpeak()) {
      toast.error("Read aloud isn't available on this browser.");
      return;
    }
    speak(text, { rate: 0.85, onEnd: () => setReadingIdx(-1) });
    setReadingIdx(i);
    setReadingQuote("");
  };

  const toggleQuoteRead = (key, text) => {
    if (readingQuote === key) { stopSpeaking(); setReadingQuote(""); return; }
    if (!canSpeak()) { toast.error("Read aloud isn't available on this browser."); return; }
    speak(text, { rate: 0.85, onEnd: () => setReadingQuote("") });
    setReadingQuote(key);
    setReadingIdx(-1);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="quiet-read-title"
      data-testid="quiet-read-reader"
      ref={scrollRef}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[hsl(var(--bg))]/85 backdrop-blur-sm px-4 py-10"
    >
      <article className="relative w-full max-w-2xl rounded-[20px] border hairline bg-[hsl(var(--paper))] p-6 sm:p-10 shadow-[0_20px_60px_-20px_hsl(var(--fg)/0.35)]">
        <button
          type="button"
          onClick={onClose}
          data-testid="quiet-read-close"
          aria-label="Close article"
          className="absolute top-3 right-3 p-1.5 rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] hover:bg-[hsl(var(--sand))]/60 tab-link z-10"
        >
          <X size={16} />
        </button>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--clay))]">
            A quiet read · {article.minutes || 4} minutes
            {article.feature_label && (
              <span
                className="ml-3 px-2 py-0.5 rounded-full bg-[hsl(var(--copper))]/12 text-[hsl(var(--copper))]"
                data-testid="quiet-read-feature-label"
              >
                {article.feature_label}
              </span>
            )}
          </div>
          {canSpeak() && (
            <button
              type="button"
              onClick={toggleFullRead}
              aria-pressed={readingFull}
              data-testid="quiet-read-listen"
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.24em] border hairline tab-link ${
                readingFull
                  ? "bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--terracotta))] border-[hsl(var(--terracotta))]/40"
                  : "text-[hsl(var(--fg))]/75 hover:text-[hsl(var(--copper))]"
              }`}
            >
              {readingFull ? <VolumeX size={12} /> : <Volume2 size={12} />}
              {readingFull ? "Stop" : "Listen"}
            </button>
          )}
        </div>
        {article.hero_image && (
          <div
            className="relative mt-4 -mx-6 sm:-mx-10 aspect-[21/4] bg-center bg-cover overflow-hidden"
            style={{ backgroundImage: `url("${article.hero_image}")` }}
            data-testid="quiet-read-hero-image"
          >
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(30,23,18,0.12) 0%, rgba(30,23,18,0.32) 100%)",
              }}
            />
          </div>
        )}
        <div className="mt-6 flex items-start gap-4 sm:gap-6">
          <SoulOrb
            size="md"
            pulseSeed={pulseSeed}
            ariaLabel="Soul orb — pulses when a reflection is carried into your Journal"
            testid="quiet-read-orb"
          />
          <div className="flex-1 min-w-0">
            <h1
              id="quiet-read-title"
              className="font-display text-3xl sm:text-4xl leading-tight tracking-tight text-[hsl(var(--fg))]"
              data-testid="quiet-read-title"
            >
              {article.title}
            </h1>
            {article.subtitle && (
              <p
                className="mt-3 font-display italic text-lg sm:text-xl text-[hsl(var(--clay))] leading-snug"
                data-testid="quiet-read-subtitle"
              >
                {article.subtitle}
              </p>
            )}
          </div>
        </div>

        {hasBookmark && (
          <button
            type="button"
            onClick={resumeBookmark}
            data-testid="quiet-read-resume"
            className="mt-6 w-full text-left px-4 py-3 rounded-md border hairline bg-[hsl(var(--sand))]/40 hover:bg-[hsl(var(--sand))]/60 tab-link"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
              Where you left off
            </div>
            <div className="mt-1 text-sm text-[hsl(var(--fg))]/80">
              Resume where you paused →
            </div>
          </button>
        )}

        <p className="mt-8 text-[15px] leading-relaxed text-[hsl(var(--fg))]/90" data-testid="quiet-read-intro">
          {article.intro}
        </p>

        {article.sections.map((s, i) => (
          <section key={s.heading} className="mt-10" data-testid={`quiet-read-section-${i}`}>
            <h2 className="font-display text-2xl sm:text-[28px] text-[hsl(var(--fg))] leading-snug tracking-tight">
              {s.heading}
            </h2>
            {s.body && (
              <p className="mt-3 text-[15px] leading-relaxed text-[hsl(var(--fg))]/85">{s.body}</p>
            )}
            {Array.isArray(s.bullets) && s.bullets.length > 0 && (
              <ul className="mt-3 space-y-2">
                {s.bullets.map((b, j) => (
                  <li key={j} className="flex gap-3 text-[15px] leading-relaxed text-[hsl(var(--fg))]/85">
                    <span aria-hidden className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-[hsl(var(--clay))]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {Array.isArray(s.subsections) && s.subsections.length > 0 && (
              <div className="mt-5 space-y-6">
                {s.subsections.map((sub, k) => (
                  <div key={sub.title} className="border-l-2 border-[hsl(var(--clay))]/30 pl-4 py-1" data-testid={`quiet-read-subsection-${i}-${k}`}>
                    <h3 className="font-display text-[17px] sm:text-lg text-[hsl(var(--fg))] leading-snug">{sub.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-[hsl(var(--fg))]/85">{sub.body}</p>
                  </div>
                ))}
              </div>
            )}
            {Array.isArray(s.quotes) && s.quotes.length > 0 && (
              <ul className="mt-4 space-y-4" data-testid={`quiet-read-quotes-${i}`}>
                {s.quotes.map((q, k) => {
                  const text = typeof q === "string" ? q : q.text;
                  const author = typeof q === "string" ? "Marcus Aurelius" : q.author;
                  const key = `${i}-${k}`;
                  const isReading = readingQuote === key;
                  return (
                    <li key={k} className="pl-5 border-l border-[hsl(var(--terracotta))]/40 group">
                      <p className="font-display italic text-[16px] sm:text-[17px] leading-snug text-[hsl(var(--fg))]">"{text}"</p>
                      <div className="mt-1.5 flex items-center justify-between gap-3">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">— {author}</div>
                        {canSpeak() && (
                          <button
                            type="button"
                            onClick={() => toggleQuoteRead(key, `${text}. — ${author}`)}
                            aria-label={isReading ? "Stop reading" : "Read aloud"}
                            data-testid={`quiet-read-quote-speaker-${i}-${k}`}
                            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] tab-link transition-opacity ${
                              isReading
                                ? "opacity-100 text-[hsl(var(--terracotta))]"
                                : "opacity-0 group-hover:opacity-100 focus:opacity-100 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
                            }`}
                          >
                            {isReading ? <VolumeX size={11} /> : <Volume2 size={11} />}
                            {isReading ? "Stop" : "Read"}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {s.closing && (
              <p className="mt-6 text-[15px] leading-relaxed text-[hsl(var(--fg))]/80 italic" data-testid={`quiet-read-closing-${i}`}>
                {s.closing}
              </p>
            )}
            {s.pull_quote && (
              <blockquote
                className="mt-8 pl-6 border-l-2 border-[hsl(var(--copper))]/60"
                data-testid={`quiet-read-pullquote-${i}`}
              >
                <p className="font-display italic text-2xl sm:text-3xl leading-snug text-[hsl(var(--clay))]">
                  “{s.pull_quote}”
                </p>
              </blockquote>
            )}
          </section>
        ))}

        {/* Reflection prompts — with per-prompt Read aloud + Seed */}
        {Array.isArray(article.reflection_prompts) && article.reflection_prompts.length > 0 && (
          <section className="mt-10 border-t hairline pt-8" data-testid="quiet-read-prompts">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
              <Feather size={11} /> Journal reflections
            </div>
            <h2 className="mt-2 font-display text-xl sm:text-2xl text-[hsl(var(--fg))]">
              Prompts to carry with you
            </h2>
            <ul className="mt-4 space-y-3">
              {article.reflection_prompts.map((p, i) => (
                <li key={i} className="p-4 rounded-md border hairline bg-[hsl(var(--sand))]/25" data-testid={`quiet-read-prompt-${i}`}>
                  <p className="font-display italic text-[16px] leading-snug text-[hsl(var(--fg))]">{p}</p>
                  <div className="mt-3 flex items-center gap-4 flex-wrap">
                    {canSpeak() && (
                      <button
                        type="button"
                        onClick={() => togglePromptRead(i, p)}
                        aria-label={readingIdx === i ? "Stop reading" : "Read this prompt aloud"}
                        aria-pressed={readingIdx === i}
                        data-testid={`quiet-read-prompt-speaker-${i}`}
                        className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] tab-link ${
                          readingIdx === i ? "text-[hsl(var(--terracotta))]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
                        }`}
                      >
                        {readingIdx === i ? <VolumeX size={11} /> : <Volume2 size={11} />}
                        {readingIdx === i ? "Stop" : "Read aloud"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => seedPrompt(p)}
                      data-testid={`quiet-read-prompt-seed-${i}`}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--clay))] hover:text-[hsl(var(--terracotta))] tab-link"
                    >
                      <Feather size={11} />
                      Seed into Journal
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Emphasized final reflection prompt — visually larger, more spacious */}
        {article.emphasized_prompt && (
          <section
            className="mt-10 border-t hairline pt-10"
            data-testid="quiet-read-emphasized-prompt"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] inline-flex items-center gap-2">
              <Feather size={11} /> A moment to reflect
            </div>
            <p className="mt-5 font-display italic text-2xl sm:text-3xl leading-snug text-[hsl(var(--fg))] max-w-xl">
              {article.emphasized_prompt}
            </p>
            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <button
                type="button"
                onClick={() => seedPrompt(article.emphasized_prompt)}
                data-testid="quiet-read-emphasized-seed"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--clay))] hover:text-[hsl(var(--terracotta))] tab-link"
              >
                <Feather size={11} />
                Seed into Journal
              </button>
            </div>
          </section>
        )}

        {/* Epilogue — a spacious closing reflection */}
        {Array.isArray(article.epilogue) && article.epilogue.length > 0 && (
          <section
            className="mt-12 border-t hairline pt-10 space-y-6"
            data-testid="quiet-read-epilogue"
          >
            {article.epilogue.map((line, i) => (
              <p
                key={i}
                className={
                  i === 0
                    ? "text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85"
                    : "font-display italic text-lg sm:text-xl leading-snug text-[hsl(var(--clay))]"
                }
                data-testid={`quiet-read-epilogue-${i}`}
              >
                {line}
              </p>
            ))}
          </section>
        )}

        <div className="mt-10 text-center">
          <button
            type="button"
            onClick={onClose}
            data-testid="quiet-read-done"
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[11px] uppercase tracking-[0.24em] bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90 tab-link"
          >
            Close the read
          </button>
        </div>
      </article>
    </div>
  );
}

/** Backward-compat reader: renders the Stoicism piece. */
export function StoicArticleReader({ open, onClose, onSeeded }) {
  return <QuietReadReader open={open} article={STOIC_ARTICLE} onClose={onClose} onSeeded={onSeeded} />;
}
